import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Shield, Mail, KeyRound, TriangleAlert as AlertTriangle, ArrowLeft, ArrowRight } from 'lucide-react';
import { trackAuth, trackError, trackEvent } from '../Utils/analytics';
import { API_BASE_URL } from '../../config/api';
import '../Admin/AdminLogin.css';

const AdminLogin: React.FC = () => {
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const navigate = useNavigate();

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError('Please enter your admin email');
      trackError('admin_login_validation_failed', 'admin_login');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setStep(2);
        trackEvent('otp_sent', 'admin_authentication', 'otp_sent');
      } else {
        const data = await response.json().catch(() => ({}));

        if (response.status === 422 && data.detail) {
          const messages = Array.isArray(data.detail)
            ? data.detail.map((d: { msg: string }) => d.msg).join('. ')
            : 'Invalid request. Please check your email.';
          setError(messages);
        } else if (response.status === 401) {
          setError('Invalid admin email. Access denied.');
          trackError('admin_login_access_denied', 'admin_login');
        } else if (response.status === 429) {
          setError('Too many attempts. Please wait before trying again.');
          trackError('admin_login_rate_limited', 'admin_login');
        } else if (response.status >= 500) {
          setError('Server error. Please try again later.');
          trackError('admin_login_server_error', 'admin_login');
        } else {
          setError(data.message || 'Failed to send OTP. Please try again.');
          trackError('admin_login_unknown_error', 'admin_login');
        }
      }
    } catch (err) {
      console.error('Admin login error:', err);
      setError('Network error. Please check your connection and try again.');
      trackError('admin_login_network_error', 'admin_login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!otp) {
      setError('Please enter the OTP sent to your email');
      trackError('admin_otp_validation_failed', 'admin_login');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      if (response.ok) {
        const data = await response.json();
        const token: string = data.token || '';

        if (token) {
          localStorage.setItem('adminToken', token);
          localStorage.setItem('adminEmail', data.admin?.email || email);
          localStorage.setItem('adminRole', data.admin?.role || 'admin');

          trackAuth('login');
          navigate('/admin/dashboard');
        } else {
          setError('Verification successful but authentication token not found.');
          trackError('admin_otp_token_missing', 'admin_login');
        }
      } else {
        const data = await response.json().catch(() => ({}));

        if (response.status === 422 && data.detail) {
          const messages = Array.isArray(data.detail)
            ? data.detail.map((d: { msg: string }) => d.msg).join('. ')
            : 'Invalid OTP. Please try again.';
          setError(messages);
          trackError('admin_otp_invalid', 'admin_login');
        } else if (response.status === 401) {
          setError('Invalid or expired OTP. Please request a new one.');
          trackError('admin_otp_expired', 'admin_login');
        } else if (response.status === 429) {
          setError('Too many attempts. Please wait before trying again.');
          trackError('admin_otp_rate_limited', 'admin_login');
        } else if (response.status >= 500) {
          setError('Server error. Please try again later.');
          trackError('admin_otp_server_error', 'admin_login');
        } else {
          setError(data.message || 'OTP verification failed. Please try again.');
          trackError('admin_otp_unknown_error', 'admin_login');
        }
      }
    } catch (err) {
      console.error('OTP verification error:', err);
      setError('Network error. Please check your connection and try again.');
      trackError('admin_otp_network_error', 'admin_login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (response.ok) {
        setOtp('');
        trackEvent('otp_resent', 'admin_authentication', 'otp_resent');
      } else {
        setError('Failed to resend OTP. Please try again.');
        trackError('admin_resend_otp_failed', 'admin_login');
      }
    } catch (err) {
      console.error('Resend OTP error:', err);
      setError('Network error. Please check your connection and try again.');
      trackError('admin_resend_otp_network_error', 'admin_login');
    } finally {
      setIsResending(false);
    }
  };

  const handleChangeEmail = () => {
    setStep(1);
    setOtp('');
    setError('');
  };

  return (
    <div className="homecookt-admin-login-page">
      <div className="homecookt-admin-login-container">

        {/* Left brand panel */}
        <div className="homecookt-admin-login-brand">
          <div className="homecookt-admin-login-brand-logo">
            <Shield size={22} />
            <span>HomeCookt Admin</span>
          </div>
          <h2>
            Secure<br />
            <em>Admin Portal</em>
          </h2>
          <p>
            Access is restricted to authorised administrators only. All sessions are encrypted and monitored.
          </p>
          <div className="homecookt-admin-login-brand-badges">
            <div className="homecookt-admin-brand-badge">
              <Shield size={15} />
              OTP-protected authentication
            </div>
            <div className="homecookt-admin-brand-badge">
              <Shield size={15} />
              Session activity logging
            </div>
            <div className="homecookt-admin-brand-badge">
              <Shield size={15} />
              Role-based access control
            </div>
          </div>
          <div className="homecookt-admin-login-brand-watermark">HC</div>
        </div>

        {/* Right form panel */}
        <div className="homecookt-admin-login-form-panel">
          <div className="homecookt-admin-login-form-inner">

            <div className="homecookt-admin-login-header">
              <h1 className="homecookt-admin-login-title">
                {step === 1 ? 'Sign in' : 'Verify identity'}
              </h1>
              <p className="homecookt-admin-login-subtitle">
                {step === 1
                  ? 'Enter your admin email to receive a one-time passcode.'
                  : `We sent a code to ${email}`}
              </p>
            </div>

            {/* Step tabs */}
            <div className="homecookt-admin-step-indicator">
              <div className={`homecookt-admin-step ${step === 1 ? 'active' : 'completed'}`}>
                <div className="homecookt-admin-step-number">1</div>
                <span className="homecookt-admin-step-label">Email</span>
              </div>
              <div className={`homecookt-admin-step ${step === 2 ? 'active' : ''}`}>
                <div className="homecookt-admin-step-number">2</div>
                <span className="homecookt-admin-step-label">Verify OTP</span>
              </div>
            </div>

            {/* Step 1: Email */}
            {step === 1 && (
              <form className="homecookt-admin-login-form" onSubmit={handleSendOtp}>
                <div className="homecookt-admin-form-group">
                  <label htmlFor="admin-email">Admin Email</label>
                  <div className="homecookt-admin-input-wrapper">
                    <input
                      id="admin-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className={error ? 'homecookt-admin-error' : ''}
                      placeholder="admin@example.com"
                      required
                      autoComplete="username"
                      autoFocus
                    />
                    <Mail className="homecookt-admin-input-icon" size={17} />
                  </div>
                </div>

                {error && (
                  <div className="homecookt-admin-error-message">
                    <AlertTriangle className="homecookt-admin-error-icon" size={17} />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" className="homecookt-admin-login-button" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="homecookt-admin-login-spinner"></div>
                      Sending code...
                    </>
                  ) : (
                    <>
                      Send OTP
                      <ArrowRight size={17} />
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Step 2: OTP */}
            {step === 2 && (
              <form className="homecookt-admin-login-form" onSubmit={handleVerifyOtp}>
                <div className="homecookt-admin-email-display">
                  <Mail size={15} />
                  <span>{email}</span>
                  <button type="button" className="homecookt-admin-change-email" onClick={handleChangeEmail}>
                    Change
                  </button>
                </div>

                <div className="homecookt-admin-form-group">
                  <label htmlFor="admin-otp">One-Time Password</label>
                  <div className="homecookt-admin-input-wrapper">
                    <input
                      id="admin-otp"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      className={error ? 'homecookt-admin-error' : ''}
                      placeholder="Enter the OTP"
                      required
                      autoComplete="one-time-code"
                      autoFocus
                      inputMode="numeric"
                    />
                    <KeyRound className="homecookt-admin-input-icon" size={17} />
                  </div>
                </div>

                {error && (
                  <div className="homecookt-admin-error-message">
                    <AlertTriangle className="homecookt-admin-error-icon" size={17} />
                    <span>{error}</span>
                  </div>
                )}

                <button type="submit" className="homecookt-admin-login-button" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <div className="homecookt-admin-login-spinner"></div>
                      Verifying...
                    </>
                  ) : (
                    <>
                      <Shield size={17} />
                      Verify &amp; Sign in
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="homecookt-admin-resend-button"
                  onClick={handleResendOtp}
                  disabled={isResending}
                >
                  {isResending ? 'Resending...' : 'Resend OTP'}
                </button>
              </form>
            )}

            {/* Security notice */}
            <div className="homecookt-admin-security-notice">
              <Shield className="homecookt-admin-security-icon" size={16} />
              <div className="homecookt-admin-security-text">
                Secure admin area. All access attempts are logged.
              </div>
            </div>

            <Link to="/" className="homecookt-admin-back-link">
              <ArrowLeft size={15} />
              Back to Homepage
            </Link>

          </div>
        </div>

      </div>
    </div>
  );
};

export default AdminLogin;
